import { api } from './api'
import {
  Appointment,
  AppointmentCreate,
  AppointmentRejectPayload,
  AvailableSlotsResponse,
  DoctorAvailabilityOverride,
  DoctorAvailabilityOverrideCreate,
  DoctorAvailabilityOverrideListResponse,
  DoctorAvailabilityRule,
  DoctorAvailabilityRuleUpdate,
  DoctorWeeklyAvailability,
  DoctorWeeklyAvailabilityCreate,
  DoctorWeeklyAvailabilityListResponse,
  DoctorListResponse,
  PaginatedResponse,
} from '@/types'

export interface AppointmentUpdate extends Partial<AppointmentCreate> {
  status?: string
}

export interface AppointmentSearchParams {
  patient_id?: string
  doctor_id?: string
  status?: string
  date_from?: string
  date_to?: string
  skip?: number
  limit?: number
}

export interface AppointmentNotificationStatus {
  enabled: boolean
  events: Array<{
    kind: 'appointment.updated' | 'appointment.reminder' | 'appointment.cancelled'
    delivery_state: string
    status: string
    scheduled_at?: string | null
    attempts: number
  }>
}

export const appointmentApi = {
  getAppointments: async (params?: AppointmentSearchParams): Promise<PaginatedResponse<Appointment>> => {
    const searchParams = new URLSearchParams()
    
    if (params?.patient_id) searchParams.append('patient_id', params.patient_id)
    if (params?.doctor_id) searchParams.append('doctor_id', params.doctor_id)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.date_from) searchParams.append('date_from', params.date_from)
    if (params?.date_to) searchParams.append('date_to', params.date_to)
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString())
    
    return api.get(`/appointments?${searchParams.toString()}`)
  },
  
  getAppointment: async (id: string): Promise<Appointment> => {
    return api.get(`/appointments/${id}`)
  },

  getNotificationStatus: async (id: string, signal?: AbortSignal): Promise<AppointmentNotificationStatus> => {
    return api.get(`/appointments/${id}/notification-status`, { signal })
  },

  getDoctors: async (): Promise<DoctorListResponse> => {
    return api.get('/appointments/doctors')
  },

  getAvailableSlots: async (
    doctorId: string,
    dateFrom: string,
    dateTo: string,
    excludeAppointmentId?: string,
    durationMinutes?: number
  ): Promise<AvailableSlotsResponse> => {
    const searchParams = new URLSearchParams()
    searchParams.append('doctor_id', doctorId)
    searchParams.append('date_from', dateFrom)
    searchParams.append('date_to', dateTo)
    if (excludeAppointmentId) searchParams.append('exclude_appointment_id', excludeAppointmentId)
    if (durationMinutes !== undefined) searchParams.append('duration_minutes', durationMinutes.toString())
    return api.get(`/appointments/available-slots?${searchParams.toString()}`)
  },

  getAvailabilityRule: async (doctorId: string): Promise<DoctorAvailabilityRule> => {
    return api.get(`/appointments/availability/rules/${doctorId}`)
  },

  upsertAvailabilityRule: async (doctorId: string, data: DoctorAvailabilityRuleUpdate): Promise<DoctorAvailabilityRule> => {
    return api.put(`/appointments/availability/rules/${doctorId}`, data)
  },

  getWeeklyAvailability: async (doctorId: string, skip = 0, limit = 100): Promise<DoctorWeeklyAvailabilityListResponse> => {
    const searchParams = new URLSearchParams()
    searchParams.append('doctor_id', doctorId)
    searchParams.append('skip', skip.toString())
    searchParams.append('limit', limit.toString())
    return api.get(`/appointments/availability/weekly?${searchParams.toString()}`)
  },

  createWeeklyAvailability: async (data: DoctorWeeklyAvailabilityCreate): Promise<DoctorWeeklyAvailability> => {
    return api.post('/appointments/availability/weekly', data)
  },

  deleteWeeklyAvailability: async (windowId: string): Promise<void> => {
    return api.delete(`/appointments/availability/weekly/${windowId}`)
  },

  getAvailabilityOverrides: async (
    doctorId: string,
    dateFrom?: string,
    dateTo?: string,
    skip = 0,
    limit = 100
  ): Promise<DoctorAvailabilityOverrideListResponse> => {
    const searchParams = new URLSearchParams()
    searchParams.append('doctor_id', doctorId)
    if (dateFrom) searchParams.append('date_from', dateFrom)
    if (dateTo) searchParams.append('date_to', dateTo)
    searchParams.append('skip', skip.toString())
    searchParams.append('limit', limit.toString())
    return api.get(`/appointments/availability/overrides?${searchParams.toString()}`)
  },

  createAvailabilityOverride: async (data: DoctorAvailabilityOverrideCreate): Promise<DoctorAvailabilityOverride> => {
    return api.post('/appointments/availability/overrides', data)
  },

  deleteAvailabilityOverride: async (overrideId: string): Promise<void> => {
    return api.delete(`/appointments/availability/overrides/${overrideId}`)
  },
  
  createAppointment: async (data: AppointmentCreate): Promise<Appointment> => {
    return api.post('/appointments', data)
  },

  updateAppointment: async (id: string, data: AppointmentUpdate): Promise<Appointment> => {
    return api.put(`/appointments/${id}`, data)
  },

  deleteAppointment: async (id: string): Promise<void> => {
    return api.delete(`/appointments/${id}`)
  },

  getPendingApprovals: async (skip = 0, limit = 100): Promise<PaginatedResponse<Appointment>> => {
    const searchParams = new URLSearchParams()
    searchParams.append('skip', skip.toString())
    searchParams.append('limit', limit.toString())
    return api.get(`/appointments/pending-approvals?${searchParams.toString()}`)
  },

  approveAppointment: async (id: string): Promise<Appointment> => {
    return api.post(`/appointments/${id}/approve`, {})
  },

  rejectAppointment: async (id: string, payload: AppointmentRejectPayload): Promise<Appointment> => {
    return api.post(`/appointments/${id}/reject`, payload)
  },
}
